/**
 * CardTool.tsx
 * 
 * 职责：
 * 1. 提供卡片工具的用户界面组件
 * 2. 管理卡片背景颜色、边框颜色和圆角的选择
 * 3. 与CardService交互，更新卡片绘制属性
 * 4. 提供用户友好的卡片工具面板
 * 5. 条件渲染工具面板，仅在卡片工具激活时显示
 * 6. 维护本地状态与CardService状态的同步
 * 7. 提供卡片创建相关的用户指引
 */

import React, { useState, useEffect } from 'react';
import { CardService } from '../services/CardService';

interface CardToolProps {
  cardService: CardService;
  isActive: boolean;
  position?: { top: number, left: number };
}

const CardTool: React.FC<CardToolProps> = ({
  cardService,
  isActive,
  position
}) => {
  // 使用React状态来跟踪当前选择的背景颜色、边框颜色和圆角
  const [backgroundColor, setBackgroundColor] = useState<string>(cardService.getBackgroundColor());
  const [borderColor, setBorderColor] = useState<string>(cardService.getBorderColor());
  // 添加动画状态
  const [isVisible, setIsVisible] = useState(false);
  
  // 当组件挂载时同步初始状态
  useEffect(() => {
    setBackgroundColor(cardService.getBackgroundColor());
    setBorderColor(cardService.getBorderColor());
  }, [cardService]);
  
  // 监听isActive变化，添加延迟显示效果
  useEffect(() => {
    if (isActive) {
      // 延迟一帧显示，确保CSS过渡生效
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 10);
      return () => clearTimeout(timer);
    } else {
      setIsVisible(false);
    }
  }, [isActive]);
  
  // 背景颜色选项
  const backgroundColorOptions = [
    '#FFFFFF', // 白色
    '#FFEB3B', // 黄色
    '#B3E5FC', // 淡蓝色
    '#C8E6C9', // 淡绿色
    '#FFCCBC', // 淡橙色
    '#E1BEE7', // 淡紫色
  ];
  
  // 边框颜色选项
  const borderColorOptions = [
    '#000000', // 黑色
    '#FFC107', // 金色
    '#2196F3', // 蓝色
    '#4CAF50', // 绿色
    '#F44336', // 红色
    '#9C27B0', // 紫色
  ];
  
  // 防止事件冒泡，阻止点击工具面板选项时自动切换回选择工具
  const handlePanelClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    // 触发自定义事件
    document.dispatchEvent(new CustomEvent('tool-panel-interaction'));
  };
  
  // 处理背景颜色变更
  const handleBackgroundColorChange = (color: string) => {
    setBackgroundColor(color);
    cardService.setBackgroundColor(color);
    // 触发自定义事件
    document.dispatchEvent(new CustomEvent('tool-panel-interaction'));
  };
  
  // 处理边框颜色变更
  const handleBorderColorChange = (color: string) => {
    setBorderColor(color);
    cardService.setBorderColor(color);
    // 触发自定义事件
    document.dispatchEvent(new CustomEvent('tool-panel-interaction'));
  };
  
  // 计算面板位置
  const panelStyle = position ? {
    top: `${position.top}px`,
    left: `${position.left}px`,
  } : {
    top: '60px',
    left: '80px',
  };
  
  // 如果工具未激活，不显示面板
  if (!isActive) {
    return null;
  }
  
  return (
    <div 
      className="card-tool-panel" 
      style={{
        position: 'absolute',
        ...panelStyle,
        backgroundColor: '#f5f5f5',
        border: '1px solid #ccc',
        borderRadius: '4px',
        padding: '10px',
        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
        zIndex: 9000,
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(-10px)',
        transition: 'opacity 0.3s ease, transform 0.3s ease',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        MozUserSelect: 'none',
        msUserSelect: 'none'
      }}
      onClick={handlePanelClick}
    >
      {/* 背景颜色选择器 */}
      <div className="background-color-selector" style={{ marginBottom: '10px' }}>
        <h4 style={{ marginBottom: '5px', fontSize: '14px' }}>背景颜色</h4>
        <div className="color-options" style={{ 
          display: 'flex', 
          flexWrap: 'wrap',
          gap: '5px'
        }}>
          {backgroundColorOptions.map((color) => (
            <div
              key={color}
              onClick={() => handleBackgroundColorChange(color)}
              style={{
                width: '20px',
                height: '20px',
                backgroundColor: color,
                border: `2px solid ${backgroundColor === color ? 'blue' : 'transparent'}`,
                borderRadius: '50%',
                cursor: 'pointer',
              }}
              title={color}
            />
          ))}
        </div>
      </div>
      
      {/* 边框颜色选择器 */}
      <div className="border-color-selector" style={{ marginBottom: '10px' }}>
        <h4 style={{ marginBottom: '5px', fontSize: '14px' }}>边框颜色</h4>
        <div className="color-options" style={{ 
          display: 'flex', 
          flexWrap: 'wrap',
          gap: '5px'
        }}>
          {borderColorOptions.map((color) => (
            <div
              key={color}
              onClick={() => handleBorderColorChange(color)}
              style={{
                width: '20px',
                height: '20px',
                backgroundColor: color,
                border: `2px solid ${borderColor === color ? 'blue' : 'transparent'}`,
                borderRadius: '50%',
                cursor: 'pointer',
              }}
              title={color}
            />
          ))}
        </div>
      </div>
      
      
    </div>
  );
};

export default CardTool; 